import type {
  ConversionProgress,
  ExtractResponse,
  PrepareDownloadResponse,
} from "./types";

const configuredBase = String(import.meta.env.VITE_API_BASE_URL || "").trim();

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ApiClient {
  private readonly baseUrl = trimTrailingSlash(configuredBase);

  resolve(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    const normalizedPath = path.startsWith("/") ? path : "/" + path;
    return this.baseUrl + normalizedPath;
  }

  async extract(url: string, signal?: AbortSignal): Promise<ExtractResponse> {
    return this.request<ExtractResponse>("/api/v1/extract", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url }),
      signal,
    });
  }

  async prepare(url: string): Promise<PrepareDownloadResponse> {
    return this.request<PrepareDownloadResponse>(url, {
      method: "GET",
      cache: "no-store",
    });
  }

  async progress(url: string): Promise<ConversionProgress> {
    return this.request<ConversionProgress>(url, {
      method: "GET",
      cache: "no-store",
    });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    let response: Response;
    try {
      response = await fetch(this.resolve(path), init);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      throw new ApiError(
        "The service could not be reached. Please try again in a moment.",
        0,
      );
    }

    if (!response.ok) {
      let message = "The request could not be completed.";
      try {
        const payload = (await response.json()) as { detail?: unknown };
        if (typeof payload.detail === "string" && payload.detail.trim()) {
          message = payload.detail;
        } else if (Array.isArray(payload.detail)) {
          message = payload.detail
            .map((item) => {
              if (typeof item === "object" && item !== null && "msg" in item) {
                return String(item.msg);
              }
              return String(item);
            })
            .join(". ");
        }
      } catch {
        // The server did not return a JSON error body.
      }
      throw new ApiError(message, response.status);
    }

    return (await response.json()) as T;
  }
}

